<!-- UI-10: annotations-page group card + grid + pager (design: ims-annotation-panel).
     Isolated from the viewer (the viewer keeps using ListAnnotationsBy). It owns the
     same data-fetch logic as ListAnnotationsBy's non-viewer path, plus selection and
     the design's collapsible card, tile grid and pager. -->
<template>
<section class="ag-card">
  <div class="ag-head">
    <div class="ag-selall" :title="$t('select-all')" @click="$emit('group-toggle', annotations)">
      <span class="ag-check" :class="groupSelState"></span>
    </div>
    <span class="ag-dot" :style="{background: dotColor}"></span>
    <h3 class="ag-title">{{ title }}</h3>
    <span class="ag-count">{{ nbAnnotations }}</span>
    <div class="ag-line"></div>
    <button class="ag-collapse" @click="collapsed = !collapsed">
      <i class="fas" :class="collapsed ? 'fa-chevron-right' : 'fa-chevron-down'"></i>
    </button>
  </div>

  <template v-if="!collapsed">
    <b-loading :is-full-page="false" class="small" :active="loading" />
    <b-message v-if="error" type="is-danger" has-icon icon-size="is-small">{{$t('failed-fetch-annots')}}</b-message>
    <em v-else-if="!annotations.length && !loading" class="ag-empty">{{ $t('no-annotation') }}</em>

    <div v-else class="ag-grid" :style="gridStyle">
      <annotation-card
        v-for="annot in annotations"
        :key="annot.id"
        :annot="annot"
        :size="size"
        :color="outlineColor"
        :show-meta="showMeta"
        :selected="selectedIds.includes(annot.id)"
        :all-terms="allTerms"
        :all-users="allUsers"
        :all-images="allImages"
        :blind-mode="blindMode"
        @open="$emit('open', $event)"
        @slide="$emit('slide', $event)"
        @delete="$emit('delete', $event)"
        @toggle="$emit('toggle', $event)"
      />
    </div>

    <div v-if="totalPages > 1" class="ag-pager">
      <div class="ag-range">{{ rangeText }}</div>
      <div class="ag-pages">
        <span class="ag-goto">{{ $t('page') }}</span>
        <input type="number" min="1" :max="totalPages" :value="currentPage"
               @change="goTo($event.target.value)" @keydown.enter="goTo($event.target.value)" class="ag-jump" />
        <span class="ag-goto">/ {{ totalPages }}</span>
        <button class="ag-arrow" :disabled="currentPage <= 1" @click="currentPage = Math.max(1, currentPage - 1)">
          <i class="fas fa-chevron-left"></i>
        </button>
        <button class="ag-arrow" :disabled="currentPage >= totalPages" @click="currentPage = Math.min(totalPages, currentPage + 1)">
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>
  </template>
</section>
</template>

<script>
import {get} from '@/utils/store-helpers';
import {AnnotationCollection} from '@/api';
import constants from '@/utils/constants';
import AnnotationCard from './AnnotationCard';

export default {
  name: 'annotation-grid',
  components: {AnnotationCard},
  props: {
    categorization: String,
    nbPerPage: Number,
    size: Number,
    outlineColor: {type: String, default: ''},
    prop: Object,
    multipleTerms: Boolean,
    noTerm: Boolean,
    multipleTracks: Boolean,
    noTrack: Boolean,
    noTag: Boolean,
    termsIds: Array,
    tracksIds: Array,
    tagsIds: Array,
    imagesIds: Array,
    usersIds: Array,
    reviewed: Boolean,
    reviewUsersIds: {type: Array, default: null},
    afterThan: {type: Number, default: null},
    beforeThan: {type: Number, default: null},
    allTerms: Array,
    allUsers: Array,
    allImages: Array,
    allTracks: Array,
    allTags: Array,
    revision: Number,
    visible: {type: Boolean, default: true},
    selectedIds: {type: Array, default: () => []},
    blindMode: {type: Boolean, default: false}
  },
  data() {
    return {
      loading: true,
      error: false,
      annotations: [],
      nbAnnotations: 0,
      currentPage: 1,
      collapsed: false,
      pendingReload: true
    };
  },
  computed: {
    project: get('currentProject/project'),
    isByTerm() {
      return this.categorization === 'TERM';
    },
    isByTrack() {
      return this.categorization === 'TRACK';
    },
    isByUser() {
      return this.categorization === 'USER';
    },
    isByImage() {
      return this.categorization === 'IMAGE';
    },
    isByTag() {
      return this.categorization === 'TAG';
    },
    isUncategorized() {
      return this.categorization === 'UNCATEGORIZED';
    },
    filteredTermsIds() {
      return this.termsIds.filter(id => id > 0);
    },
    filteredTracksIds() {
      return this.tracksIds.filter(id => id > 0);
    },
    tooManyImages() {
      return this.project.numberOfImages > constants.MAX_IMAGES_FOR_FILTER;
    },
    showMeta() {
      return this.size >= 120;
    },
    dotColor() {
      if (this.isByTerm && this.prop && this.prop.color) {
        return this.prop.color;
      }
      return '#1e63d0';
    },
    gridStyle() {
      return {
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, minmax(${this.size}px, 1fr))`,
        gap: (this.size < 120) ? '10px' : '16px'
      };
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.nbAnnotations / this.nbPerPage));
    },
    rangeText() {
      let start = (this.currentPage - 1) * this.nbPerPage;
      return `${this.$t('results')}: ${start + 1}–${Math.min(start + this.nbPerPage, this.nbAnnotations)} / ${this.nbAnnotations}`;
    },
    annotationIds() {
      return this.annotations.map(a => a.id);
    },
    groupSelState() {
      if (!this.annotationIds.length) {
        return 'none';
      }
      let sel = this.annotationIds.filter(id => this.selectedIds.includes(id)).length;
      if (sel === 0) {
        return 'none';
      }
      return (sel >= this.annotationIds.length) ? 'all' : 'some';
    },
    title() {
      if (this.isByUser) {
        return this.prop.fullName;
      } else if (this.isByImage) {
        return this.prop.blindedName ? this.prop.blindedName : this.prop.instanceFilename;
      } else if (this.isByTerm && this.multipleTerms) {
        return this.$t('multiple-terms');
      } else if (this.isByTerm && this.noTerm) {
        return this.$t('no-term');
      } else if (this.isByTrack && this.multipleTracks) {
        return this.$t('multiple-tracks');
      } else if (this.isByTrack && this.noTrack) {
        return this.$t('no-track');
      } else if (this.isByTag && this.noTag) {
        return this.$t('no-tag');
      } else if (this.isUncategorized) {
        return this.$t('all-annotations-uncategorized');
      }
      return this.prop.name;
    },
    collection() {
      this.revision; // reload on revision bump
      return new AnnotationCollection({
        term: (this.isByTerm && !this.multipleTerms && !this.noTerm) ? this.prop.id : null,
        track: (this.isByTrack && !this.multipleTracks && !this.noTrack) ? this.prop.id : null,
        tag: (this.isByTag && !this.noTag) ? this.prop.id : null,
        user: (this.isByUser) ? this.prop.id : null,
        image: (this.isByImage) ? this.prop.id : null,

        noTerm: this.noTerm,
        multipleTerm: this.multipleTerms,
        noTrack: this.noTrack,
        multipleTrack: this.multipleTracks,
        noTag: this.noTag,

        terms: (!this.isByTerm && (this.noTerm || this.multipleTerms || (this.filteredTermsIds.length > 0 && this.filteredTermsIds.length < this.allTerms.length))) ? this.filteredTermsIds : null,
        users: (!this.isByUser && this.usersIds && this.usersIds.length > 0) ? this.usersIds : null,
        images: (!this.isByImage && ((this.tooManyImages && this.imagesIds.length > 0) || (!this.tooManyImages && this.imagesIds.length > 0 && this.imagesIds.length < this.allImages.length))) ? this.imagesIds : null,
        tracks: (!this.isByTrack && (this.noTrack || this.multipleTracks || this.filteredTracksIds.length < this.allTracks.length)) ? this.filteredTracksIds : null,
        tags: (!this.isByTag && this.tagsIds) ? this.tagsIds.filter(id => id > 0) : null,

        reviewed: this.reviewed,
        reviewUsers: (!this.isByUser) ? this.reviewUsersIds : null,
        showTerm: true,
        showGIS: true,
        showTrack: true,
        showLink: true,
        showImageGroup: true,
        showImage: this.tooManyImages,
        showSlice: true,
        afterThan: this.afterThan,
        beforeThan: this.beforeThan,
        max: this.nbPerPage,
        project: this.project.id
      });
    }
  },
  watch: {
    currentPage() {
      this.fetchPage();
    },
    collection() {
      this.currentPage = 1;
      this.fetchPage();
    },
    visible(value) {
      if (value && this.pendingReload) {
        this.fetchPage();
      }
    }
  },
  methods: {
    goTo(value) {
      let n = parseInt(value, 10);
      if (isNaN(n)) {
        return;
      }
      this.currentPage = Math.max(1, Math.min(this.totalPages, n));
    },
    async fetchPage() {
      if (!this.visible) {
        this.pendingReload = true;
        return;
      }
      this.pendingReload = false;

      if ((!this.tooManyImages && !this.imagesIds.length) || (!this.reviewed && !this.usersIds.length)
        || (this.reviewed && !this.reviewUsersIds.length) || !this.termsIds.length || !this.tracksIds.length
        || (this.tagsIds ? !this.tagsIds.length : 0)) {
        this.annotations = [];
        this.nbAnnotations = 0;
        this.loading = false;
        return;
      }

      this.loading = true;
      try {
        let data = await this.collection.fetchPage(this.currentPage - 1);
        this.annotations = data.array;
        this.nbAnnotations = data.totalNbItems;
      } catch (error) {
        if (this.currentPage > 1) {
          this.currentPage = 1;
          return;
        }
        console.log(error);
        this.nbAnnotations = 0;
        this.error = true;
      }
      this.loading = false;
    }
  },
  async created() {
    await this.fetchPage();
  }
};
</script>

<style scoped>
.ag-card {
  position: relative;
  border: 1px solid #e8eaef;
  border-radius: 16px;
  padding: 18px 20px 20px;
  margin-bottom: 18px;
  background: #fff;
}
.ag-head { display: flex; align-items: center; gap: 13px; margin-bottom: 16px; }
.ag-selall { cursor: pointer; display: flex; }
.ag-check {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 1.7px solid #cbd2dc;
  background: #fff;
  position: relative;
}
.ag-check.all, .ag-check.some { background: #1e63d0; border-color: #1e63d0; }
.ag-check.all::after {
  content: '';
  position: absolute;
  left: 6px;
  top: 2px;
  width: 6px;
  height: 11px;
  border: solid #fff;
  border-width: 0 2.6px 2.6px 0;
  transform: rotate(45deg);
}
.ag-check.some::after {
  content: '';
  position: absolute;
  left: 4.5px;
  top: 8.5px;
  width: 9px;
  height: 2.6px;
  background: #fff;
  border-radius: 2px;
}
.ag-dot { width: 11px; height: 11px; border-radius: 50%; flex: none; }
.ag-title { margin: 0; font-size: 14px; font-weight: 800; letter-spacing: 0.05em; color: #1a2233; text-transform: uppercase; }
.ag-count {
  background: #f0f2f6;
  color: #5a6473;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  font-variant-numeric: tabular-nums;
}
.ag-line { flex: 1; height: 1px; background: #eef0f4; }
.ag-collapse {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #eceef2;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #7a8494;
}
.ag-collapse:hover { background: #f4f6fa; }
.ag-empty { color: #9aa3b0; font-style: italic; }
.ag-grid { width: 100%; }
.ag-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 18px;
  flex-wrap: wrap;
}
.ag-range { font-size: 12.5px; color: #9aa3b0; font-weight: 600; }
.ag-pages { display: flex; align-items: center; gap: 8px; }
.ag-goto { font-size: 12.5px; color: #9aa3b0; font-weight: 600; white-space: nowrap; }
.ag-jump {
  width: 54px;
  height: 32px;
  border: 1px solid #e2e6ec;
  border-radius: 8px;
  padding: 0 8px;
  font-size: 12.5px;
  font-weight: 600;
  color: #3a4252;
  text-align: center;
}
.ag-arrow {
  min-width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e2e6ec;
  background: #fff;
  color: #5a6473;
  cursor: pointer;
}
.ag-arrow:disabled { opacity: 0.4; cursor: default; }
.ag-arrow:not(:disabled):hover { background: #f4f6fa; }
</style>
